import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'cms_access_token';
const REFRESH_KEY = 'cms_refresh_token';

// Configure axios to send cookies (standalone window)
axios.defaults.withCredentials = true;

// Hydrate the Authorization header from localStorage on load so cookie-less
// contexts — notably the Outlook add-in iframe, where browsers block
// third-party cookies — can still authenticate via a Bearer token.
const savedToken = (typeof window !== 'undefined') ? localStorage.getItem(TOKEN_KEY) : null;
if (savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// Persist (or clear) the access/refresh tokens in localStorage and keep the
// axios default Authorization header in sync.
const setAuthTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
  }
  if (refreshToken !== undefined) {
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    else localStorage.removeItem(REFRESH_KEY);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);
  const [pending2FA, setPending2FA] = useState(null); // Stores temp_token when 2FA is required

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    
    if (data.requires_2fa) {
      setPending2FA(data.temp_token);
      return { requires_2fa: true };
    }
    
    setAuthTokens(data.access_token, data.refresh_token);
    setUser(data);
    setPending2FA(null);
    // Fire-and-forget: keep the local Sellsy opps index in sync with the 25
    // most recent opportunities in Sellsy. Safe to fail silently.
    axios.post(`${API_URL}/api/admin/opportunities/index/sync`).catch(() => {});
    return data;
  };

  const verify2FA = async (code) => {
    if (!pending2FA) {
      throw new Error("Pas de session 2FA en attente");
    }
    
    const { data } = await axios.post(`${API_URL}/api/auth/verify-2fa`, {
      code,
      temp_token: pending2FA
    });
    
    setAuthTokens(data.access_token, data.refresh_token);
    setUser(data);
    setPending2FA(null);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } finally {
      setAuthTokens(null, null);
      setUser(false);
      setPending2FA(null);
    }
  };

  const setup2FA = async () => {
    const { data } = await axios.post(`${API_URL}/api/auth/setup-2fa`);
    return data;
  };

  const enable2FA = async (code) => {
    const { data } = await axios.post(`${API_URL}/api/auth/enable-2fa`, { code });
    setUser(prev => ({ ...prev, totp_enabled: true }));
    return data;
  };

  const disable2FA = async (code) => {
    const { data } = await axios.post(`${API_URL}/api/auth/disable-2fa`, { code });
    setUser(prev => ({ ...prev, totp_enabled: false }));
    return data;
  };

  const refreshToken = async () => {
    try {
      const stored = localStorage.getItem(REFRESH_KEY);
      const { data } = await axios.post(`${API_URL}/api/auth/refresh`, stored ? { refresh_token: stored } : {});
      if (data?.access_token) setAuthTokens(data.access_token);
      return true;
    } catch (e) {
      setAuthTokens(null, null);
      setUser(false);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      pending2FA,
      login,
      verify2FA,
      register,
      logout,
      setup2FA,
      enable2FA,
      disable2FA,
      refreshToken,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export default AuthContext;
